import React from 'react'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import {
  Sheet,
  SheetCloseButton,
  SheetOverlay,
  SheetContent,
  SheetTitle,
} from '@wartek-id/sheet-v1'
import { List, ListItem } from '@wartek-id/list'
import { useToast } from '@wartek-id/toast'
import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'

import { useProfile } from 'app/ProfileRevampPage/hooks/useProfile'
import { useSETrackEvent } from 'app/ProfileRevampPage/hooks/useSETrackEvent'
import { prependCountryCode } from 'utils/phone'

import MessageIcon from 'assets/icons/message.svg'
import WhatsappIcon from 'assets/icons/whatsapp.svg'
import FileCopyIcon from 'assets/icons/file-copy.svg'

interface IContactDrawerProps {
  isOpen: boolean
  onClose: () => void
  onShare: () => void
}

export const ContactDrawer = ({
  isOpen,
  onClose,
  onShare,
}: IContactDrawerProps) => {
  const toast = useToast()
  const { profileData, isNarasumberContactable } = useProfile()
  const { SETrackClickContactItem, SETrackCloseContactDrawer } =
    useSETrackEvent()
  const {
    name,
    narasumber: { email, phone, whatsapp },
  } = profileData

  // Contact by Email
  const emailSubject = encodeURI('Halo Guru Narasumber')
  const emailBody = encodeURI(`Halo Bapak/Ibu ${name},
      Perkenalkan saya [masukkan nama] dari sekolah [masukkan sekolah]
      Saya mendapat kontak Anda sebagai narasumber Praktik Baik IKM dari platform Merdeka Mengajar.
      
      Saya tertarik untuk mendengar cerita Anda saat menerapkan IKM dan berharap dapat inspirasi maupun kiat untuk saya terapkan. Apakah Anda bersedia untuk berbagi praktik baik IKM di sekolah kami?
    `)
  const emailUrl = Boolean(email)
    ? `mailto:${email}?subject=${emailSubject}&body=${emailBody}`
    : null

  // Contact by Whatsapp
  const whatsappText = encodeURIComponent(`Halo Bapak/Ibu ${name},
    kenalkan saya [masukkan nama] dari sekolah [masukkan sekolah]

    Saya mendapat kontak Anda dari platform Merdeka Mengajar.

    Saya tertarik untuk mendengar cerita Anda saat menerapkan IKM. Apakah Anda bersedia untuk berbagi praktik baik IKM di sekolah kami?
  `)
  const whatsappUrl = Boolean(whatsapp)
    ? `https://wa.me/${prependCountryCode(whatsapp)}?text=${whatsappText}`
    : null

  const onContactItemClicked = (contactType) => {
    SETrackClickContactItem({
      contactType,
    })

    if (contactType === 'clipboard') {
      toast({ message: 'Berhasil disalin', type: 'success' })
    }

    if (contactType === 'share_profile') {
      onShare()
    }

    onClose()
  }

  const onCloseButtonClicked = () => {
    onClose()
    SETrackCloseContactDrawer()
  }

  return (
    <Sheet isOpen={isOpen} onClose={onClose} sheetSize="auto">
      <SheetOverlay />
      <SheetContent className="max-w-xl m-auto !z-[1000]">
        <SheetCloseButton onClick={onCloseButtonClicked} />
        <SheetTitle bold bordered>
          {isNarasumberContactable
            ? 'Hubungi Narasumber'
            : 'Ingin hubungi narasumber?'}
        </SheetTitle>
        <div className="px-4 pb-2">
          <div className="py-3">
            <Text variant="body">
              {isNarasumberContactable ? (
                <>
                  Ingin tahu praktik baik dalam menerapkan Implementasi
                  Kurikulum Merdeka di sekolah/komunitas belajar Anda? Hubungi
                  atau undang narasumber untuk dengar cerita dan dapatkan
                  kiatnya.
                  <br />
                  <br />
                  Catatan: Narasumber akan merespon sesuai dengan ketersediaan
                  jadwal mereka
                </>
              ) : (
                <>
                  Minta bantuan Kepala Sekolah atau Penggerak Komunitas untuk
                  menghubungi atau mengundang narasumber sebagai pembicara ke
                  sekolah/komunitas belajar Anda.
                </>
              )}
            </Text>
          </div>
          <List type="menu" bold>
            {isNarasumberContactable ? (
              <>
                {Boolean(email) && (
                  <ListItem
                    as="a"
                    target="_blank"
                    style={{ padding: '12px 8px', width: '100%' }}
                    href={emailUrl}
                    title="email"
                    onClick={() => onContactItemClicked('email')}
                  >
                    <div className="flex flex-row space-x-3 cursor-pointer">
                      <MessageIcon />
                      <Text variant="action">Hubungi lewat email</Text>
                    </div>
                  </ListItem>
                )}

                {Boolean(whatsapp) && (
                  <ListItem
                    as="a"
                    target="_blank"
                    style={{ padding: '12px 8px', width: '100%' }}
                    href={whatsappUrl}
                    title="whatsapp"
                    onClick={() => onContactItemClicked('whatsapp')}
                  >
                    <div className="flex flex-row space-x-3 cursor-pointer">
                      <WhatsappIcon />
                      <Text variant="action">Hubungi lewat Whatsapp</Text>
                    </div>
                  </ListItem>
                )}

                {Boolean(phone) && (
                  <CopyToClipboard
                    text={profileData?.narasumber?.phone}
                    onCopy={() => onContactItemClicked('clipboard')}
                  >
                    <ListItem>
                      <div className="flex flex-row space-x-3 cursor-pointer">
                        <FileCopyIcon />
                        <Text variant="heading-sm">Salin nomor telepon</Text>
                      </div>
                    </ListItem>
                  </CopyToClipboard>
                )}
              </>
            ) : (
              <>
                <ListItem
                  style={{ padding: '12px 8px', width: '100%' }}
                  title="share"
                  onClick={() => onContactItemClicked('share_profile')}
                >
                  <div className="flex flex-row space-x-3 items-center cursor-pointer">
                    <Icon>share</Icon>
                    <Text variant="action">
                      Bagikan profil ke Kepala Sekolah atau Penggerak Komunitas
                    </Text>
                  </div>
                </ListItem>
              </>
            )}
          </List>
        </div>
      </SheetContent>
    </Sheet>
  )
}
